package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.GlossaryTermDto
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.i18n.t
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.coroutines.awaitCancellation

internal data class GlossaryPageData(
    val project: ProjectDetailDto,
    val terms: List<GlossaryTermDto>,
)

@Page("/app/projects/{slug}/glossary")
@Layout(".components.layouts.AppLayout")
@Composable
fun ProjectGlossaryPage() {
    val slug = rememberPageContext().route.params["slug"].orEmpty()
    val verifiedRoute = liveProjectRoute(slug)
    val authority = ProjectAuthority.resolve(verifiedRoute?.project, Session.organizations)
    val authoritySubject = verifiedRoute?.authoritySubject(Session.organizations)
    val structureClaim = ProjectActionClaim.capture(
        verifiedRoute,
        Session.organizations,
        ProjectPrivilege.STRUCTURE,
    )
    var refreshKey by remember(authoritySubject) { mutableStateOf(0) }
    // **`loadReloadable`, keyed on the subject with the refresh as a token.** As a key, `refreshKey`
    // discarded the terms before the request was even sent: adding one blanked the whole glossary
    // to a skeleton, on an action the reader had just taken and while they were reading the list
    // they were working through.
    val state = loadReloadable(authoritySubject, reloadToken = refreshKey) {
        val subject = authoritySubject
        if (subject == null) {
            // The route is still being verified. `requireNotNull` here turned that wait into
            // `Async.Failure("Required value was null.")`, which `ErrorState` printed to the reader
            // verbatim. Waiting is a wait; the changed key restarts the load when it resolves.
            awaitCancellation()
        }
        requestProjectResource(
            key = subject.action,
            expectedSlug = slug,
            memberships = Session.organizations,
        ) { route ->
            GlossaryPageData(route.project, ApiClient.listGlossaryTerms(route.project.slug))
        }
    }
    val toasts = rememberToasts()

    Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
        when (val snapshot = state.state) {
            Async.Loading -> {
                PageHeader(t("app.projects.slug.project_glossary_page.glossary", "Glossary"), t("app.projects.slug.project_glossary_page.define_the_product_terms_every_translator", "Define the product terms every translator and AI provider should follow."))
                LoadingState(rows = 5)
            }
            is Async.Failure -> {
                PageHeader(t("app.projects.slug.project_glossary_page.glossary", "Glossary"), t("app.projects.slug.project_glossary_page.define_the_product_terms_every_translator", "Define the product terms every translator and AI provider should follow."))
                // `AsyncFailureState`, not `ErrorState`: it is the one that reads the failure
                // rather than assuming a fault. A refusal becomes "You don't have access to the
                // glossary" with no button, and a deleted project "That isn't here any more" —
                // where `ErrorState` offered Retry on both, which re-runs a request the server has
                // already refused. That is the Retry `AsyncFailureState` exists to remove.
                AsyncFailureState(
                    snapshot,
                    subject = t("app.projects.slug.project_glossary_page.the_glossary", "the glossary"),
                    onRetry = { refreshKey++ },
                )
            }
            is Async.Success -> GlossaryContent(
                data = snapshot.value,
                authority = authority,
                actionStateKey = authoritySubject,
                mutationClaim = structureClaim,
                isMutationCurrent = {
                    structureClaim?.remainsAuthorized(
                        liveProjectRoute(slug),
                        Session.organizations,
                    ) == true
                },
                slug = slug,
                onChanged = { refreshKey++ },
                notify = toasts::show,
            )
        }
    }
    ToastHost(toasts)
}
